import { capitalize, compact } from "lodash-es"
import { useTranslation } from "react-i18next"
import { convertSubunitIntoUnit } from "@oysterhr/common"
import { getVariableCompensationValue } from "~/shared/components/VariableCompensationSummary/helpers"
import { Money, VariableCompensation } from "@oysterhr/schema"
import { QuoteCollapsableItem } from "../QuoteCollapsableItem"
import { QuoteItem } from "../QuoteItem"
import { QuoteItemGroup } from "../QuoteItemGroup"
import { getAmountWithCurrencyAndCommas } from "../../utils/helpers"
import { BenefitsDetails, QuoteCycle, VariableCompensationsGroup } from "../../types"

type QuoteAdditionalCostsProps = {
  currencyCode: string
  salary: Money
  optionalCosts?: VariableCompensationsGroup
  totalAdditionalCosts: number
  benefitsDetails?: BenefitsDetails
  cycle: QuoteCycle
}

const QuoteAdditionalCosts = ({
  salary,
  totalAdditionalCosts,
  currencyCode,
  optionalCosts,
  benefitsDetails,
  cycle,
}: QuoteAdditionalCostsProps) => {
  const { t } = useTranslation("components/Quote")

  const getBenefitsAdditionalCosts = () => {
    if (!benefitsDetails) return

    const {
      selectedBenefitPackage,
      selectedBenefitAddons,
      selectedBenefitFee,
      selectedPlanFee,
      hasEssentialPackage,
    } = benefitsDetails

    const benefitsCosts = []

    if (selectedBenefitPackage && !hasEssentialPackage) {
      benefitsCosts.push(selectedBenefitPackage)
      selectedBenefitFee && benefitsCosts.push(selectedBenefitFee)
    }

    if (selectedBenefitAddons && selectedBenefitAddons.amount > 0) {
      benefitsCosts.push(selectedBenefitAddons)
      selectedPlanFee && benefitsCosts.push(selectedPlanFee)
    }

    return benefitsCosts
  }

  // Derived variables
  const benefitsCosts = getBenefitsAdditionalCosts()

  if (!totalAdditionalCosts) return null

  return (
    <QuoteCollapsableItem
      key="variableCompensations"
      value={getAmountWithCurrencyAndCommas(totalAdditionalCosts, currencyCode, cycle)}
      isCategory
      label={<div className="flex items-center">{t("labelOptionalCosts")}</div>}
      items={compact([
        optionalCosts
          ? Object.entries(optionalCosts).map(([key, value]) => {
              if (value.length) {
                return (
                  <QuoteItemGroup
                    label={capitalize(key)}
                    key={key}
                    items={value.map((item: VariableCompensation) => (
                      <QuoteItem
                        className="mb-0 text-xs text-gray-400"
                        key={item.id}
                        label={t(
                          `${key === "allowances" ? "ALLOWANCE" : "BONUS"}.labelOptions.${
                            item.label
                          }`,
                          {
                            ns: "variable_compensation/Common",
                          },
                        )}
                        value={getAmountWithCurrencyAndCommas(
                          convertSubunitIntoUnit(
                            getVariableCompensationValue(item, salary),
                            currencyCode,
                          ),
                          currencyCode,
                          cycle,
                        )}
                      />
                    ))}
                  />
                )
              }
            })
          : null,
        benefitsCosts?.length ? (
          <QuoteItemGroup
            label="Benefits"
            key="benefits"
            items={benefitsCosts.map(benefit => (
              <QuoteItem
                className="mb-0 text-xs text-gray-400"
                key={benefit.name}
                label={benefit.name}
                value={getAmountWithCurrencyAndCommas(benefit.amount, currencyCode, cycle)}
              />
            ))}
          />
        ) : null,
      ])}
      toggleDataAttributes={{
        "data-analytics-name": "employer-additional-cost-selector",
        "data-testid": "additional-cost-collapse",
      }}
    />
  )
}

export default QuoteAdditionalCosts
